import React, { useState } from 'react';
import { PrimaryButton, SecondaryButton } from './buttons';

interface SelectiveResendModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedEmails: string[]) => void;
  isLoading: boolean;
  collaborators: Array<{
    name: string;
    email?: string;
    status: 'expired' | 'pending' | 'signed';
    role: 'composer' | 'author' | 'both';
    share: number;
    signed?: boolean; // Keep for backward compatibility
  }>;
}

const SelectiveResendModal: React.FC<SelectiveResendModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  collaborators
}) => {
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);

  if (!isOpen) return null;

  const collaboratorsWithEmail = collaborators.filter(c => c.email);
  const unsignedCollaborators = collaboratorsWithEmail.filter(c => c.status !== 'signed');

  const handleSelectCollaborator = (email: string, checked: boolean) => {
    if (checked) {
      setSelectedEmails([...selectedEmails, email]);
    } else {
      setSelectedEmails(selectedEmails.filter(e => e !== email));
    }
  };

  const handleSelectAll = () => {
    const unsignedEmails = unsignedCollaborators.map(c => c.email!);
    setSelectedEmails(unsignedEmails);
  };

  const handleSelectNone = () => {
    setSelectedEmails([]);
  };

  const handleConfirm = () => {
    onConfirm(selectedEmails);
    setSelectedEmails([]);
  };

  const handleClose = () => {
    setSelectedEmails([]);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-[#27272A] rounded-2xl p-6 w-full max-w-2xl mx-4 border border-[#3F3F46] max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center">
            <svg className="w-5 h-5 mr-2 text-[#14B8A6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Resend Invites to Selected
          </h3>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="text-[#6B7280] hover:text-white transition-colors disabled:opacity-50"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-[#9CA3AF] text-sm">
                Select unconfirmed collaborators to resend invites to. Only unconfirmed collaborators' status will be reset.
              </p>
              <p className="text-[#6B7280] text-xs mt-1">
                {unsignedCollaborators.length} of {collaboratorsWithEmail.length} collaborators available for resend
              </p>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleSelectAll}
                disabled={isLoading || unsignedCollaborators.length === 0}
                className="text-xs px-2 py-1 text-[#14B8A6] border border-[#14B8A6]/30 rounded-lg hover:bg-[#14B8A6]/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Select All Unconfirmed
              </button>
              <button
                onClick={handleSelectNone}
                disabled={isLoading}
                className="text-xs px-2 py-1 text-[#9CA3AF] border border-[#3F3F46] rounded-lg hover:bg-[#3F3F46] transition-colors disabled:opacity-50"
              >
                Select None
              </button>
            </div>
          </div>

          <div className="space-y-2 max-h-80 overflow-y-auto">
            {collaboratorsWithEmail.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-[#6B7280]">No collaborators with email addresses found.</p>
              </div>
            ) : unsignedCollaborators.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-green-400">🎉 All collaborators have already signed!</p>
                <p className="text-[#6B7280] text-sm mt-2">No invites need to be resent.</p>
              </div>
            ) : (
              collaboratorsWithEmail.map((collaborator) => (
              <div
                key={collaborator.email}
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  collaborator.status === 'signed'
                    ? 'bg-[#1F1F22] border-[#3F3F46] opacity-60'
                    : 'bg-[#1F1F22] border-[#3F3F46]'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={selectedEmails.includes(collaborator.email!)}
                    onChange={(e) => handleSelectCollaborator(collaborator.email!, e.target.checked)}
                    disabled={isLoading || collaborator.status === 'signed'}
                    className="w-4 h-4 text-[#14B8A6] bg-[#18181B] border-[#3F3F46] rounded focus:ring-[#14B8A6] focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <div>
                    <p className={`font-medium ${collaborator.status === 'signed' ? 'text-[#6B7280]' : 'text-white'}`}>
                      {collaborator.name}
                    </p>
                    <p className={`text-sm capitalize ${collaborator.status === 'signed' ? 'text-[#6B7280]' : 'text-[#9CA3AF]'}`}>
                      {collaborator.role} • {collaborator.share}%
                    </p>
                    <p className="text-xs text-[#6B7280]">
                      {collaborator.email}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  {collaborator.status === 'signed' ? (
                    <div className="flex flex-col items-end">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-300 border border-green-500/30">
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Signed
                      </span>
                      <span className="text-xs text-gray-500 mt-1">Cannot resend</span>
                    </div>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-300 border border-yellow-500/30">
                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                      </svg>
                      Pending
                    </span>
                  )}
                </div>
              </div>
              ))
            )}
          </div>

          {selectedEmails.length > 0 && (
            <div className="bg-[#14B8A6]/10 border border-[#14B8A6]/20 rounded-lg p-3 mt-4">
              <p className="text-[#14B8A6] text-sm">
                ⚠️ This will reset confirmations for {selectedEmails.length} selected collaborator{selectedEmails.length !== 1 ? 's' : ''} and send new invite emails.
              </p>
            </div>
          )}
        </div>

        <div className="flex space-x-3">
          <SecondaryButton
            onClick={handleClose}
            disabled={isLoading}
            className="flex-1"
          >
            Cancel
          </SecondaryButton>
          <PrimaryButton
            onClick={handleConfirm}
            disabled={isLoading || selectedEmails.length === 0}
            loading={isLoading}
            className="flex-1"
          >
            {`Resend to Selected (${selectedEmails.length})`}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

export default SelectiveResendModal;
